import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Box, Flex, Input, Text } from "@chakra-ui/react";
import { RootState } from "../../app/mainReducer";
import {
  PerguntaQuestionarioSatisfacao,
  RespostaQuestionarioSatisfacaoItem,
} from "../types/questionario_satisfacao";

type Props = {
  onChange: (respostas: RespostaQuestionarioSatisfacaoItem[]) => void;
};

const QuestionarioSatisfacao = ({ onChange }: Props) => {
  const pesquisa = useSelector((state: RootState) => state.questionarioSatisfacao.pesquisa);
  const [respostas, setRespostas] = useState<Record<string, RespostaQuestionarioSatisfacaoItem>>({});

  const perguntas: PerguntaQuestionarioSatisfacao[] = pesquisa?.perguntas ?? [];
  const r10Ukey = pesquisa?.ukey ?? "";

  useEffect(() => {
    onChange(Object.values(respostas));
  }, [respostas]);

  const selecionaEstrela = (pergunta: PerguntaQuestionarioSatisfacao, alternativaUkey: string) => {
    setRespostas((atual) => ({
      ...atual,
      [pergunta.ukey]: {
        r10_ukey: r10Ukey,
        r11_ukey: pergunta.ukey,
        r12_ukey: alternativaUkey,
      },
    }));
  };

  const atualizaObservacao = (pergunta: PerguntaQuestionarioSatisfacao, texto: string) => {
    const alternativaUnica = pergunta.alternativas[0];
    setRespostas((atual) => ({
      ...atual,
      [pergunta.ukey]: {
        r10_ukey: r10Ukey,
        r11_ukey: pergunta.ukey,
        r12_ukey: alternativaUnica?.ukey ?? "",
        resposta_texto: texto,
      },
    }));
  };

  const estrelaSelecionada = (
    pergunta: PerguntaQuestionarioSatisfacao,
    alternativa: { codigo: string }
  ) => {
    const resposta = respostas[pergunta.ukey];
    if (!resposta?.r12_ukey) return false;
    const selecionada = pergunta.alternativas.find((a) => a.ukey == resposta.r12_ukey);
    if (!selecionada) return false;
    return Number(alternativa.codigo) <= Number(selecionada.codigo);
  };

  if (!r10Ukey) {
    return null;
  }

  const blocos: { estrela: PerguntaQuestionarioSatisfacao; observacao?: PerguntaQuestionarioSatisfacao }[] = [];
  for (let i = 0; i < perguntas.length; i++) {
    const pergunta = perguntas[i];
    if (pergunta.tipo_resposta == "estrelas") {
      const proxima = perguntas[i + 1];
      const observacao =
        proxima && proxima.tipo_resposta != "estrelas" ? proxima : undefined;
      if (observacao) {
        i += 1;
      }
      blocos.push({ estrela: pergunta, observacao });
    } else {
      blocos.push({ estrela: pergunta });
    }
  }

  return (
    <Flex
      direction="column"
      gap={3}
      mb={4}
      p={3}
      bg="white"
      color="gray.800"
      borderWidth="1px"
      borderColor="gray.200"
      borderRadius="md"
    >
      <Text fontSize="md" fontWeight="bold" color="gray.800">
        Avaliação do cliente
      </Text>
      <Text fontSize="sm" color="gray.800">
        Clique nas estrelas para avaliar.
      </Text>
      {blocos.map(({ estrela, observacao }) => (
        <Box key={estrela.ukey}>
          <Text fontWeight="semibold" fontSize="sm" mb={1} color="gray.800">
            {estrela.descricao}
          </Text>
          {estrela.tipo_resposta == "estrelas" ? (
            <Flex gap={1} mb={observacao ? 1 : 0}>
              {estrela.alternativas.map((alternativa) => (
                <Box
                  as="button"
                  type="button"
                  key={alternativa.ukey}
                  onClick={() => selecionaEstrela(estrela, alternativa.ukey)}
                  fontSize="2xl"
                  lineHeight="1"
                  color={estrelaSelecionada(estrela, alternativa) ? "#ed8936" : "#cbd5e0"}
                  aria-label={alternativa.descricao}
                >
                  ★
                </Box>
              ))}
            </Flex>
          ) : null}
          {observacao ? (
            <Input
              size="xs"
              h="28px"
              mt={1}
              bg="white"
              color="gray.800"
              borderColor="gray.300"
              placeholder="Observações? Escreva aqui... (opcional)"
              value={respostas[observacao.ukey]?.resposta_texto ?? ""}
              onChange={(e) => atualizaObservacao(observacao, e.target.value)}
            />
          ) : null}
          {estrela.tipo_resposta != "estrelas" ? (
            <Input
              size="xs"
              h="28px"
              bg="white"
              color="gray.800"
              borderColor="gray.300"
              placeholder="Observações? Escreva aqui... (opcional)"
              value={respostas[estrela.ukey]?.resposta_texto ?? ""}
              onChange={(e) => atualizaObservacao(estrela, e.target.value)}
            />
          ) : null}
        </Box>
      ))}
    </Flex>
  );
};

export default QuestionarioSatisfacao;
